(() => {
  const formSelector = '[data-product-card-atc]';

  const getVariants = (form) => {
    if (form.cardVariants) return form.cardVariants;

    const script = form.querySelector('[data-card-variants]');
    form.cardVariants = script ? JSON.parse(script.textContent || '[]') : [];
    return form.cardVariants;
  };

  const selectedOptions = (form) =>
    Array.from(form.querySelectorAll('[data-card-option]')).map((select) => select.value);

  const matchingVariant = (variants, options) =>
    variants.find((variant) => variant.options.every((value, index) => value === options[index]));

  const colorFromName = (name = '') => {
    const normalized = name.trim().toLowerCase();
    if (normalized === 'black & white' || normalized === 'black and white') {
      return 'url("https://cdn.shopify.com/s/files/1/1016/7661/3997/files/Black_White.jpg?v=1787188168") center / cover';
    }

    const cssColor = normalized.replace(/\s+/g, '');
    if (window.CSS?.supports?.('color', cssColor)) return cssColor;

    let hash = 0;
    for (let index = 0; index < normalized.length; index += 1) {
      hash = normalized.charCodeAt(index) + ((hash << 5) - hash);
    }
    return `hsl(${Math.abs(hash) % 360} 28% 48%)`;
  };

  const updateSwatchColor = (dot) => {
    if (!dot || dot.dataset.swatchReady) return;
    dot.dataset.swatchReady = 'true';
    dot.style.background = colorFromName(dot.dataset.colorName);
  };

  const variantImageUrl = (variant) =>
    variant?.featured_media?.preview_image?.src ||
    variant?.featured_media?.src ||
    variant?.featured_image?.src ||
    variant?.image?.src ||
    '';

  const updateCardImage = (form, variant) => {
    const imageUrl = variantImageUrl(variant);
    if (!imageUrl) return;

    const card = form.closest('article') || form.closest('[class*="card"]');
    const image = card?.querySelector(
      '.minus-product-card__image, .shop-product-card__image, .minus-search-result__image, .home-banner-card__image, .home-best-feature__image'
    );
    if (!image) return;

    image.src = imageUrl;
    image.srcset = '';
    if (variant.featured_image?.alt) image.alt = variant.featured_image.alt;
  };

  const updateCartCount = async () => {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) return;

    const cart = await response.json();
    document.querySelectorAll('.site-header__cart-count').forEach((count) => {
      count.textContent = cart.item_count;
      count.hidden = cart.item_count === 0;
    });
    document.dispatchEvent(new CustomEvent('minus:cart-updated', { detail: cart }));
  };

  const setStatus = (form, message) => {
    const status = form.querySelector('[data-card-atc-status]');
    if (status) status.textContent = message || '';
  };

  const updateForm = (form, settings = {}) => {
    const variants = getVariants(form);
    const options = selectedOptions(form);
    const selectedVariant = matchingVariant(variants, options);
    const idInput = form.querySelector('[data-card-variant-id]');
    const button = form.querySelector('[data-card-add-button]');
    const buttonText = form.querySelector('[data-card-add-text]');

    form.querySelectorAll('[data-card-option]').forEach((select) => {
      const optionIndex = Number(select.dataset.cardOption);

      Array.from(select.options).forEach((option) => {
        const optionSet = [...options];
        optionSet[optionIndex] = option.value;
        const hasAvailableVariant = variants.some((variant) =>
          variant.available && variant.options.every((value, index) => value === optionSet[index])
        );
        option.disabled = !hasAvailableVariant;
      });
    });

    form.querySelectorAll('[data-card-color-button]').forEach((button) => {
      const optionIndex = Number(button.dataset.cardOptionIndex);
      const select = form.querySelector(`[data-card-option="${optionIndex}"]`);
      const matchingOption = Array.from(select?.options || []).find((option) => option.value === button.dataset.cardColorValue);
      const isSelected = form.dataset.cardColorTouched === 'true' && select?.value === button.dataset.cardColorValue;

      button.disabled = Boolean(matchingOption?.disabled);
      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      updateSwatchColor(button.querySelector('[data-card-swatch-dot]'));
    });

    if (idInput && selectedVariant) idInput.value = selectedVariant.id;
    if (selectedVariant && settings.updateImage) updateCardImage(form, selectedVariant);

    const canAdd = Boolean(selectedVariant?.available);
    if (button) button.disabled = !canAdd;
    if (buttonText) buttonText.textContent = canAdd ? 'Add to cart' : 'Sold out';
    setStatus(form, '');
  };

  const initForm = (form) => {
    if (form.dataset.cardAtcReady) return;

    form.dataset.cardAtcReady = 'true';
    form.querySelectorAll('[data-card-swatch-dot]').forEach(updateSwatchColor);
    updateForm(form, { updateImage: false });
  };

  const initAll = (root = document) => {
    root.querySelectorAll(formSelector).forEach(initForm);
  };

  document.addEventListener('change', (event) => {
    const select = event.target instanceof Element ? event.target.closest('[data-card-option]') : null;
    if (!select) return;

    updateForm(select.closest(formSelector), { updateImage: true });
  });

  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-card-color-button]') : null;
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();

    const form = button.closest(formSelector);
    const select = form?.querySelector(`[data-card-option="${button.dataset.cardOptionIndex}"]`);
    if (!form || !select || button.disabled) return;

    form.dataset.cardColorTouched = 'true';
    select.value = button.dataset.cardColorValue;
    updateForm(form, { updateImage: true });
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest(formSelector);
    if (!form) return;

    event.preventDefault();
    event.stopPropagation();

    const button = form.querySelector('[data-card-add-button]');
    const buttonText = form.querySelector('[data-card-add-text]');
    const originalText = buttonText?.textContent || 'Add to cart';
    const variantId = form.querySelector('[data-card-variant-id]')?.value;

    if (!variantId || button?.disabled) return;

    if (button) button.disabled = true;
    if (buttonText) buttonText.textContent = 'Adding...';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });

      if (!response.ok) throw new Error('Add to cart failed');

      await updateCartCount();
      setStatus(form, 'Added to cart');
      if (buttonText) buttonText.textContent = 'Added';
    } catch (error) {
      setStatus(form, 'Could not add. Please try again.');
      if (buttonText) buttonText.textContent = originalText;
    } finally {
      window.setTimeout(() => {
        updateForm(form, { updateImage: false });
      }, 1200);
    }
  });

  document.addEventListener('DOMContentLoaded', () => initAll());
  initAll();

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) initAll(node);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
